/**
 * A list's state: page, size, sort, search, filters and the queue chosen, as
 * the list endpoint standard has them (docs/list-endpoints.md) and as the URL
 * carries them, so a filtered list or a queue is a link a skill can give.
 *
 * Choosing a queue REPLACES the filters with the queue's params; touching any
 * filter afterwards drops the queue marker (the list is no longer that queue).
 * Any change but paging goes back to page 1. Everything stays server-side:
 * this only shapes the request.
 *
 * A list reached through a record's related list also carries `prefill`: the
 * filters that came from that record. The list endpoint never sees it; it tells
 * the page that the object's collection commands (Add note…) start with those
 * values fixed (`related-prefill.ts`). Changing one of those filters by hand
 * means the list is no longer that record's, so it leaves `prefill`.
 *
 * A list whose manifest marks a queue `default` opens on it: a link that names no queue, filter or search is read as
 * that queue. "All" is the explicit way back, written `queue=all`, so it survives a reload and a shared link.
 */
import type { ManifestList, ManifestQueue } from "./contract";

export type FilterValue = string | string[];

export interface ListQuery {
  page: number;
  page_size: number;
  sort: string | null;
  q: string | null;
  filters: Record<string, FilterValue>;
  /** The queue the filters came from, until a filter is changed by hand. */
  queue: string | null;
  /** The filters set from a parent record's related list, still unchanged. */
  prefill: string[];
  /** "All" was chosen on purpose, so the list's default queue does not apply. */
  all: boolean;
}

/** The queue marker that means "All": the list's default queue does not apply. */
export const ALL_QUEUES = "all";

export const DEFAULT_PAGE_SIZE = 25;

/** Params the URL carries that are not filters. */
const RESERVED = new Set([
  "page",
  "page_size",
  "sort",
  "q",
  "queue",
  "prefill",
]);

export function emptyListQuery(): ListQuery {
  return {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    sort: null,
    q: null,
    filters: {},
    queue: null,
    prefill: [],
    all: false,
  };
}

/** The queue the list opens on when the link names none, or null when the list declares no default. */
export function defaultQueue(list: ManifestList): ManifestQueue | null {
  return list.queues.find((q) => q.default) ?? null;
}

function isArrayFilter(list: ManifestList, name: string): boolean {
  return list.filters.find((f) => f.param === name)?.kind === "multi_enum";
}

function positiveInt(value: string | null, fallback: number): number {
  const n = value === null ? NaN : Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

/**
 * Read a list's state from a query string. Params that are neither standard
 * nor a declared filter are dropped (the API answers 422 to unknown params)
 * and reported, so a stale link degrades to the unfiltered list, loudly.
 */
export function parseListQuery(search: string, list: ManifestList): ListQuery {
  const params = new URLSearchParams(search);
  const query = emptyListQuery();
  query.page = positiveInt(params.get("page"), 1);
  query.page_size = positiveInt(params.get("page_size"), DEFAULT_PAGE_SIZE);
  query.sort = params.get("sort") || null;
  query.q = params.get("q") || null;
  const queueId = params.get("queue");
  query.queue =
    queueId && list.queues.some((q) => q.id === queueId) ? queueId : null;
  query.all = queueId === ALL_QUEUES;
  const dropped: string[] = [];
  for (const name of new Set(params.keys())) {
    if (RESERVED.has(name)) continue;
    if (!list.filters.some((f) => f.param === name)) {
      dropped.push(name);
      continue;
    }
    const values = params.getAll(name).filter((v) => v !== "");
    if (values.length === 0) continue;
    query.filters[name] = isArrayFilter(list, name) ? values : values[0]!;
  }
  query.prefill = [...new Set(params.getAll("prefill"))].filter(
    (name) => name in query.filters,
  );
  if (dropped.length > 0) {
    console.warn("object_ui.list.params_dropped", {
      list: list.path,
      params: dropped,
    });
  }
  const fallback = defaultQueue(list);
  const namesNothing =
    !query.queue &&
    !query.all &&
    !query.q &&
    Object.keys(query.filters).length === 0;
  return fallback && namesNothing
    ? { ...query, ...queueState(fallback) }
    : query;
}

/** Write a list's state as a query string, leaving out defaults. */
export function formatListQuery(query: ListQuery, list?: ManifestList): string {
  const params = new URLSearchParams();
  if (query.queue) params.set("queue", query.queue);
  // "All" needs saying only where the list has a default queue and nothing else in the link overrides it.
  else if (
    query.all &&
    (!list || defaultQueue(list)) &&
    !query.q &&
    Object.keys(query.filters).length === 0
  )
    params.set("queue", ALL_QUEUES);
  for (const [name, value] of Object.entries(query.filters)) {
    for (const v of Array.isArray(value) ? value : [value])
      params.append(name, v);
  }
  for (const name of query.prefill) params.append("prefill", name);
  if (query.q) params.set("q", query.q);
  if (query.sort) params.set("sort", query.sort);
  if (query.page_size !== DEFAULT_PAGE_SIZE)
    params.set("page_size", String(query.page_size));
  if (query.page !== 1) params.set("page", String(query.page));
  return params.toString();
}

/** The params the list endpoint is called with (the queue marker is the UI's, not the API's). */
export function toRequestParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("page_size", String(query.page_size));
  if (query.sort) params.set("sort", query.sort);
  if (query.q) params.set("q", query.q);
  for (const [name, value] of Object.entries(query.filters)) {
    for (const v of Array.isArray(value) ? value : [value])
      params.append(name, v);
  }
  return params;
}

function queueFilters(queue: ManifestQueue): Record<string, FilterValue> {
  return Object.fromEntries(
    Object.entries(queue.params).map(([name, value]) => [
      name,
      Array.isArray(value) ? value.map(String) : String(value),
    ]),
  );
}

function queueState(
  queue: ManifestQueue | null,
): Pick<ListQuery, "filters" | "queue" | "prefill" | "all"> {
  return {
    filters: queue ? queueFilters(queue) : {},
    queue: queue ? queue.id : null,
    prefill: [],
    all: false,
  };
}

export function withQueue(
  query: ListQuery,
  queue: ManifestQueue | null,
): ListQuery {
  return { ...query, page: 1, ...queueState(queue) };
}

/** Every row, search cleared: the "All" chip. On a list with a default queue it is said in the link (`queue=all`). */
export function withAllRows(query: ListQuery, list: ManifestList): ListQuery {
  return {
    ...withQueue({ ...query, q: null }, null),
    all: defaultQueue(list) !== null,
  };
}

/**
 * Narrowing taken away until the link names nothing is still "All", not a return to the default queue: the person
 * chose to leave it.
 */
function keepAllWhenBare(query: ListQuery): ListQuery {
  const isBare =
    !query.queue && !query.q && Object.keys(query.filters).length === 0;
  return isBare ? { ...query, all: true } : query;
}

export function withFilter(
  query: ListQuery,
  name: string,
  value: FilterValue | null,
): ListQuery {
  const filters = { ...query.filters };
  if (
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  )
    delete filters[name];
  else filters[name] = value;
  return keepAllWhenBare({
    ...query,
    page: 1,
    filters,
    queue: null,
    all: false,
    prefill: query.prefill.filter((n) => n !== name),
  });
}

export function withSearch(query: ListQuery, q: string): ListQuery {
  return keepAllWhenBare({ ...query, page: 1, q: q.trim() === "" ? null : q });
}

export function withSort(query: ListQuery, sort: string | null): ListQuery {
  return { ...query, page: 1, sort };
}

/**
 * How many things narrow the list beyond search and the queue chosen: filters
 * set by hand (a queue's own filters are the queue, not extra) and a sort.
 */
export function narrowingCount(query: ListQuery): number {
  return (
    (query.queue ? 0 : Object.keys(query.filters).length) + (query.sort ? 1 : 0)
  );
}

/**
 * Whether the list is narrowed to some of its records by a queue, a filter or a search: only then is there a set of
 * records to step through ("Work through these"), not the whole object.
 */
export function isListNarrowed(query: ListQuery): boolean {
  return (
    query.queue !== null || Object.keys(query.filters).length > 0 || !!query.q
  );
}

/** The list with every hand-set filter and the sort gone; a queue's own filters and search stay. */
export function withNarrowingCleared(query: ListQuery): ListQuery {
  return keepAllWhenBare({
    ...query,
    page: 1,
    sort: null,
    filters: query.queue ? query.filters : {},
    prefill: query.queue ? query.prefill : [],
  });
}

export function withPage(query: ListQuery, page: number): ListQuery {
  return { ...query, page };
}

/** The list query a queue opens on, for counts and links. */
export function queueQuery(queue: ManifestQueue): ListQuery {
  return withQueue(emptyListQuery(), queue);
}
